import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type Check = 'idle' | 'checking' | 'valid' | 'invalid';

type TokenPayload = {
  exp?: number;
  scope?: string;
  sub?: string;
};

const SCOPE_LABELS: Record<string, string> = {
  openid: 'Confirm your identity',
  email: 'Share your email address',
  profile: 'Share your basic profile',
};

const decodePayload = (token?: string): TokenPayload | null => {
  if (!token) return null;
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return json as TokenPayload;
  } catch {
    return null;
  }
};

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-border/50 py-2 text-sm last:border-0">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right font-mono break-all">{value}</span>
  </div>
);

const AuthStatus: React.FC = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [check, setCheck] = useState<Check>('idle');
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    supabase.auth.getSession().then(({ data, error: sessionError }) => {
      if (sessionError) setError(`Sitzung konnte nicht gelesen werden: ${sessionError.message}`);
      setSession(data.session);
      setLoading(false);
    });
    const timer = window.setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => {
      sub.subscription.unsubscribe();
      window.clearInterval(timer);
    };
  }, []);

  const verifyToken = useCallback(async () => {
    setCheck('checking');
    setError(null);
    const { data, error: userError } = await supabase.auth.getUser();
    if (userError || !data.user) {
      setCheck('invalid');
      setError(
        userError
          ? `Token wurde vom Server abgelehnt: ${userError.message}`
          : 'Token wurde vom Server abgelehnt: kein Nutzer zurückgegeben.',
      );
      return;
    }
    setCheck('valid');
  }, []);

  const signOut = async () => {
    const { error: signOutError } = await supabase.auth.signOut();
    if (signOutError) setError(`Abmelden fehlgeschlagen: ${signOutError.message}`);
    setCheck('idle');
  };

  const payload = decodePayload(session?.access_token);
  const exp = payload?.exp ?? null;
  const secondsLeft = exp ? exp - now : null;
  const expired = secondsLeft !== null && secondsLeft <= 0;
  const scopes = (payload?.scope ?? '').split(/\s+/).filter(Boolean);

  return (
    <div className="min-h-screen bg-background p-6 flex items-center justify-center">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>OAuth-Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {loading ? (
            <p className="text-sm text-muted-foreground">Status wird geladen…</p>
          ) : (
            <>
              <div className="flex items-center gap-2 text-sm">
                <span
                  aria-hidden
                  className={`h-2.5 w-2.5 rounded-full ${
                    session && !expired ? 'bg-emerald-500' : session ? 'bg-amber-500' : 'bg-destructive'
                  }`}
                />
                <span className="font-medium">
                  {session ? (expired ? 'Angemeldet, Token abgelaufen' : 'Angemeldet') : 'Nicht angemeldet'}
                </span>
              </div>

              {session ? (
                <div>
                  <Row label="Nutzer-ID" value={session.user.id} />
                  <Row label="E-Mail" value={session.user.email ?? '—'} />
                  <Row label="Anmeldeart" value={session.user.app_metadata?.provider ?? '—'} />
                  <Row
                    label="Token gültig bis"
                    value={exp ? new Date(exp * 1000).toLocaleString('de-CH') : 'unbekannt'}
                  />
                  <Row
                    label="Restlaufzeit"
                    value={
                      secondsLeft === null
                        ? 'unbekannt'
                        : expired
                          ? 'abgelaufen'
                          : `${Math.floor(secondsLeft / 60)} min ${secondsLeft % 60} s`
                    }
                  />
                  <Row
                    label="Serverprüfung"
                    value={
                      check === 'idle'
                        ? 'nicht geprüft'
                        : check === 'checking'
                          ? 'läuft…'
                          : check === 'valid'
                            ? 'Token gültig'
                            : 'Token ungültig'
                    }
                  />
                  <div className="border-t border-border/50 pt-3">
                    <span className="text-sm text-muted-foreground">Gewährte OAuth-Scopes</span>
                    {scopes.length > 0 ? (
                      <ul className="mt-1 space-y-1 text-sm">
                        {scopes.map((scope) => (
                          <li key={scope} className="flex items-start gap-2">
                            <span aria-hidden className="text-emerald-500">✓</span>
                            <span>{SCOPE_LABELS[scope] ?? scope}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-1 text-sm">Keine Scopes im Token enthalten.</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Es besteht keine aktive Sitzung. Melde dich an, um Nutzer-ID und Token-Status zu sehen.
                </p>
              )}

              {error && (
                <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </p>
              )}

              <div className="flex flex-wrap gap-3">
                {session ? (
                  <>
                    <Button onClick={verifyToken} disabled={check === 'checking'}>
                      Token prüfen
                    </Button>
                    <Button variant="outline" onClick={signOut}>
                      Abmelden
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => navigate('/auth?next=%2Fauth%2Fstatus')}>Anmelden</Button>
                )}
              </div>

              <p className="text-xs text-muted-foreground">
                Der Zugriffs-Token wird bewusst nicht angezeigt.{' '}
                <Link className="underline" to="/">
                  Zur Startseite
                </Link>
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthStatus;
